import Translation from "src/translations/locales/translation";

import { t } from "i18next";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { RoutesEnum } from "src/enums/Routes";
import { withTranslation } from "react-i18next";
import { Form } from "src/shared-components/form";
import { WithTFunction } from "src/types/WithTFunction";
import { PasswordValidator } from "src/components/password";
import { DynamicForm } from "src/composable/base-dynamic-form";
import { FirebaseAuthErrorHandler } from "src/utils/FirebaseAuthErrorHandler";
import { ArrowSquareLeftIcon } from "src/assets/icons/components/ArrowSquareLeftIcon";
import { SimpleElevatedButton } from "src/shared-components/form/fields/buttons/simple-elevated-button";
import { SimplePasswordInput } from "src/shared-components/form/fields/text-fields/simple-password-input";

import {
  UpdatePasswordScreenFormProps,
  UpdatePasswordScreenFormState,
} from "./index.interfaces";

class UpdatePasswordScreenForm extends DynamicForm<
  WithTFunction<UpdatePasswordScreenFormProps>,
  UpdatePasswordScreenFormState
> {
  _passwordValidator: PasswordValidator;

  state: Readonly<UpdatePasswordScreenFormState> = {
    isLoading: false,
  };

  constructor(props: WithTFunction<UpdatePasswordScreenFormProps>) {
    super(props);
    this._passwordValidator = new PasswordValidator(props.t);
  }

  onDisableSubmitButton = (): boolean => {
    return false;
  };

  onBackToSignInScreenButtonClicked = () => {
    window.location.href = RoutesEnum.signin;
  };

  onSubmit = async () => {
    const { password } = this.props;

    // validate the password
    if (!password.trim()) return;
    if (this._passwordValidator.validatePassword(password)) return;

    try {
      await this.props.onResetPasswordButtonClicked();
    } catch (e) {
      FirebaseAuthErrorHandler.handle(e, this.props.t);
    }
  };

  onConfirmPasswordValidate = (value: string) => {
    if (value !== this.props.password)
      return t(Translation.screens.updatePassword.form.passwordsMustMatch);
    return null;
  };

  render(): ReactNode {
    const {
      t,
      password,
      confirmPassword,
      onPasswordChanged,
      onConfirmPasswordChanged,
    } = this.props;

    return (
      <section className="flex flex-col gap-8 desktop:gap-20 w-full max-w-[300px] py-16 tablet:py-28 desktop:py-0 desktop:min-w-[50vw] desktop:px-[12.5%]">
        {/* back to sign-in screen button */}
        <Link
          to={RoutesEnum.signin}
          className="cursor-pointer w-fit h-fit flex items-center gap-3"
        >
          <ArrowSquareLeftIcon className="stroke-on-background" />
          <h1 className="hidden tablet:block">
            {t(Translation.screens.updatePassword.backToSigninScreenButton)}
          </h1>
        </Link>
        <Form
          className="flex flex-col gap-6 w-full py-16 tablet:py-28 desktop:py-0"
          onSubmit={this.onSubmit}
        >
          {/* title and description */}
          <div className="flex flex-col gap-3">
            {/* title */}
            <h1 className="text-lg font-new-atten-bold">
              {t(Translation.screens.updatePassword.form.title).toUpperCase()}
            </h1>
            {/* description */}
            <p className="opacity-50 text-normal">
              {t(Translation.screens.updatePassword.form.subTitle)}
            </p>
          </div>
          {/* password input */}
          <SimplePasswordInput
            id="updatePassword-password-input"
            name="updatePassword-password-input"
            placeholder={t(
              Translation.screens.updatePassword.form.passwordPlaceholder
            )}
            value={password}
            onChange={onPasswordChanged}
            maxLength={100}
            validator={this._passwordValidator.validatePassword}
          />
          {/* confirm password input */}
          <SimplePasswordInput
            id="updatePassword-confirmPassword-input"
            name="updatePassword-confirmPassword-input"
            placeholder={t(
              Translation.screens.updatePassword.form.confirmPasswordPlaceholder
            )}
            value={confirmPassword}
            onChange={onConfirmPasswordChanged}
            maxLength={100}
            validator={this.onConfirmPasswordValidate}
          />
          {/* submit button */}
          <SimpleElevatedButton
            id="updatePassword-submit-button"
            text={t(Translation.screens.updatePassword.form.submitButton)}
            onClick={this.onSubmit}
            disabled={false}
          />
        </Form>
      </section>
    );
  }
}

export default withTranslation()(UpdatePasswordScreenForm);
